import React, { useState, useEffect } from 'react';
import Dialog from '../../../../common/element/Dialog';
import Upload from '../../../../common/element/Upload';
import BtnPrimary from '../../../../common/element/Buttons/btnPrimary';
import ThreeDotLoader from "../../../../common/element/ButtonLoader/ThreeDotLoader";
import Select from '../../../../common/element/Select';
import Button from '../../../../common/element/Buttons';

const AddEditQuiz = ({ dialogOpen, onDialogClose, quizData, mode = 'add' }) => {
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [formData, setFormData] = useState({
    quizName: '',
    questions: [{
      id: Date.now(),
      questionType: null,
      question: '',
      options: ['', '', '', ''],
      answer: '',
      ratingScale: "",
      attachments: [],
      errors: {}
    }]
  });

  const [formError, setFormErrors] = useState({
    quizName: '',
    questions: ''
  });

  useEffect(() => {
    if (quizData && (mode === 'edit' || mode === 'view')) {
      const processedQuestions = (quizData.questions || []).map(q => ({
        ...q,
        questionType: q.questionType ? questionTypes.find(type => type.value === q.questionType) : null
      }));

      setFormData({
        quizName: quizData.quizName || '',
        questions: processedQuestions.length > 0 ? processedQuestions : [{
          id: Date.now(),
          questionType: null,
          question: '',
          options: ['', '', '', ''],
          answer: '',
          ratingScale: 5,
          attachments: [],
          errors: {}
        }]
      });
    }
  }, [quizData, mode]);

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';

  const questionTypes = [
    { value: 'mcq', label: 'MCQ' },
    { value: 'subjective', label: 'Subjective' },
    { value: 'multi-select', label: 'Multi Select' },
    { value: 'rating', label: 'Rating' }
  ];

  const handleInputChange = (field, value) => {
    if (isViewMode) return;

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (formError[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const addNewQuestion = () => {
    if (isViewMode) return;

    const newQuestion = {
      id: Date.now(),
      questionType: null,
      question: '',
      options: ['', '', '', ''],
      answer: '',
      ratingScale: 5,
      attachments: [],
      errors: {}
    };

    setFormData(prev => ({
      ...prev,
      questions: [...prev.questions, newQuestion]
    }));
  };

  const updateQuestion = (questionId, field, value) => {
    if (isViewMode) return;

    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map(q => {
        if (q.id === questionId) {
          const updated = { ...q, [field]: value };
          // Clear error for this field
          if (updated.errors && updated.errors[field]) {
            updated.errors = { ...updated.errors, [field]: '' };
          }
          return updated;
        }
        return q;
      })
    }));
  };

  const updateQuestionOption = (questionId, optionIndex, value) => {
    if (isViewMode) return;

    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map(q => {
        if (q.id === questionId) {
          const newOptions = [...q.options];
          newOptions[optionIndex] = value;
          const updated = { ...q, options: newOptions };
          if (updated.errors && updated.errors.options) {
            updated.errors = { ...updated.errors, options: '' };
          }
          return updated;
        }
        return q;
      })
    }));
  };

  const removeQuestion = (questionId) => {
    if (isViewMode) return;

    if (formData.questions.length > 1) {
      setFormData(prev => ({
        ...prev,
        questions: prev.questions.filter(q => q.id !== questionId)
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.quizName.trim()) {
      errors.quizName = 'Quiz name is required';
    }

    let hasQuestionErrors = false;
    const updatedQuestions = formData.questions.map(q => {
      const qErrors = {};

      if (!q.questionType) {
        qErrors.questionType = 'Question type is required';
      }

      if (!q.question.trim()) {
        qErrors.question = 'Question is required';
      }

      if (q.questionType?.value === 'mcq' || q.questionType?.value === 'multi-select') {
        const filledOptions = q.options.filter(opt => opt.trim());
        if (filledOptions.length < 2) {
          qErrors.options = 'At least 2 options are required';
        }
      }

      if (q.questionType?.value === 'subjective') {
        if (!q.answer.trim()) {
          qErrors.answer = 'Answer is required';
        }
      }

      if (q.questionType?.value === 'rating') {
        if (!q.ratingScale || q.ratingScale < 1 || q.ratingScale > 10) {
          qErrors.ratingScale = 'Rating scale must be between 1 and 10';
        }
      }

      if (Object.keys(qErrors).length > 0) {
        hasQuestionErrors = true;
      }

      return { ...q, errors: qErrors };
    });

    if (hasQuestionErrors) {
      errors.questions = 'Please fix errors in questions';
      setFormData(prev => ({ ...prev, questions: updatedQuestions }));
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0 && !hasQuestionErrors;
  };

  const handleSubmit = () => {
    if (isViewMode) return;

    if (validateForm()) {
      setButtonDisabled(true);

      // Convert questionType objects back to string values for submission
      const submissionData = {
        ...formData,
        questions: formData.questions.map(q => ({
          ...q,
          questionType: q.questionType?.value || ''
        }))
      };

      console.log('Form submitted:', submissionData);

      setTimeout(() => {
        setButtonDisabled(false);
        onDialogClose();
      }, 2000);
    }
  };

  const getTitle = () => {
    if (isViewMode) return 'View Quiz';
    if (isEditMode) return 'Edit Quiz';
    return 'Add New Quiz';
  };

  const renderQuestionFields = (question, index) => {
    const questionTypeValue = question.questionType?.value;

    switch (questionTypeValue) {
      case 'mcq':
      case 'multi-select':
        return (
          <>
            {/* Question */}
            <div className="flex mb-5">
              <label className="font-medium text-gray-700 text-sm w-1/3 pt-2">
                Question*
              </label>
              <div className="w-2/3 pl-2 relative">
                <input
                  className="h-9 pl-3 w-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  value={question.question}
                  placeholder="Question"
                  onChange={(e) => updateQuestion(question.id, 'question', e.target.value)}
                  disabled={isViewMode}
                />

                {question.errors?.question && (
                  <p className="text-xs text-red-600 mt-1">{question.errors.question}</p>
                )}
              </div>
            </div>

            {/* Options */}
            <div className="flex mb-5">
              <label className="font-medium text-gray-700 text-sm w-1/3 pt-2"></label>
              <div className="w-2/3 pl-2">
                <div className="grid grid-cols-2 gap-3">
                  {question.options.map((option, optIndex) => (
                    <div key={optIndex} className="relative">
                      <input
                        className="h-9 pl-3 w-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        value={option}
                        placeholder={String.fromCharCode(65 + optIndex)}
                        onChange={(e) => updateQuestionOption(question.id, optIndex, e.target.value)}
                        disabled={isViewMode}
                      />
                    </div>
                  ))}
                </div>
                {question.errors?.options && (
                  <p className="text-xs text-red-600 mt-1">{question.errors.options}</p>
                )}
              </div>
            </div>
          </>
        );

      case 'subjective':
        return (
          <>
            {/* Question */}
            <div className="flex mb-5">
              <label className="font-medium text-gray-700 text-sm w-1/3 pt-2">
                Question*
              </label>
              <div className="w-2/3 pl-2 relative">
                <input
                  className="h-9 pl-3 w-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  value={question.question}
                  placeholder="Question"
                  onChange={(e) => updateQuestion(question.id, 'question', e.target.value)}
                  disabled={isViewMode}
                />

                {question.errors?.question && (
                  <p className="text-xs text-red-600 mt-1">{question.errors.question}</p>
                )}
              </div>
            </div>

            {/* Answer */}
            <div className="flex mb-5">
              <label className="font-medium text-gray-700 text-sm w-1/3 pt-2">
                Answer*
              </label>
              <div className="w-2/3 pl-2">
                <textarea
                  className="h-9 pl-3 w-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  value={question.answer}
                  placeholder="Enter answer"
                  onChange={(e) => updateQuestion(question.id, 'answer', e.target.value)}
                  disabled={isViewMode}
                />
                {question.errors?.answer && (
                  <p className="text-xs text-red-600 mt-1">{question.errors.answer}</p>
                )}
              </div>
            </div>
          </>
        );

      case 'rating':
        return (
          <>
            {/* Question */}
            <div className="flex mb-5">
              <label className="font-medium text-gray-700 text-sm w-1/3 pt-2">
                Question*
              </label>
              <div className="w-2/3 pl-2 relative">
                <input
                  className="h-9 pl-3 w-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  value={question.question}
                  placeholder="Question"
                  onChange={(e) => updateQuestion(question.id, 'question', e.target.value)}
                  disabled={isViewMode}
                />

                {question.errors?.question && (
                  <p className="text-xs text-red-600 mt-1">{question.errors.question}</p>
                )}
              </div>
            </div>

            {/* Rating Scale */}
            <div className="flex mb-5">
              <label className="font-medium text-gray-700 text-sm w-1/3 pt-2">
                Rating Scale*
              </label>
              <div className="w-2/3 pl-2">
                <input
                  type="number"
                  min="1"
                  max="10"
                  className="h-9 pl-3 w-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  value={question.ratingScale}
                  placeholder="Enter rating scale (1-10)"
                  onChange={(e) => updateQuestion(question.id, 'ratingScale', parseInt(e.target.value) || '')}
                  disabled={isViewMode}
                />
                {question.errors?.ratingScale && (
                  <p className="text-xs text-red-600 mt-1">{question.errors.ratingScale}</p>
                )}
              </div>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      isOpen={dialogOpen}
      onClose={onDialogClose}
      width={800}
      onRequestClose={onDialogClose}
      bodyOpenClassName="overflow-hidden"
    >
      <div className="flex border-b pb-4 px-6 pt-6 items-center justify-between">
        <div className="flex items-center">
          <span className="h-10 w-10 border rounded-lg border-gray-200 flex items-center justify-center shadow-sm mr-3">
            <svg width="18" height="21" viewBox="0 0 18 21" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6.90669 0.000175795C6.75047 0.00439411 6.60647 0.0845422 6.52168 0.214373L5.14895 2.25191H0.473684C0.212116 2.25191 0 2.4618 0 2.72062V7.40764C0 7.66646 0.212116 7.87634 0.473684 7.87634H5.21053C5.47209 7.87634 5.68421 7.66646 5.68421 7.40764V3.70967L4.73684 5.11578V6.93894H0.947368V3.18932H4.51724L3.18496 5.16668L2.60271 4.01433C2.48855 3.78138 2.20519 3.68408 1.96977 3.79704C1.73435 3.91 1.63601 4.19037 1.75017 4.42332C1.75187 4.42669 1.75348 4.43007 1.75528 4.43344L2.70265 6.30825C2.81965 6.53979 3.10415 6.63372 3.33824 6.51795C3.41195 6.48148 3.47476 6.42664 3.52052 6.35878L7.30999 0.734351C7.45825 0.520998 7.40368 0.229184 7.18806 0.0824799C7.10536 0.0263294 7.00693 -0.00254268 6.90669 0.000175795ZM9 4.12691C8.73843 4.12691 8.52632 4.3368 8.52632 4.59561V5.53302C8.52632 5.79183 8.73843 6.00172 9 6.00172H17.5263C17.7879 6.00172 18 5.79183 18 5.53302V4.59561C18 4.3368 17.7879 4.12691 17.5263 4.12691H9ZM0.473684 8.81721C0.212116 8.81721 0 9.0271 0 9.28592V13.9729C0 14.2318 0.212116 14.4416 0.473684 14.4416H5.21053C5.47209 14.4416 5.68421 14.2318 5.68421 13.9729V9.28592C5.68421 9.0271 5.47209 8.81721 5.21053 8.81721H0.473684ZM0.947368 9.75462H4.73684V13.5042H0.947368V9.75462ZM9 10.6886C8.73843 10.6886 8.52632 10.8984 8.52632 11.1573V12.0947C8.52632 12.3535 8.73843 12.5634 9 12.5634H17.5263C17.7879 12.5634 18 12.3535 18 12.0947V11.1573C18 10.8984 17.7879 10.6886 17.5263 10.6886H9ZM0.473684 15.3756C0.212116 15.3756 0 15.5855 0 15.8443V20.5313C0 20.7901 0.212116 21 0.473684 21H5.21053C5.47209 21 5.68421 20.7901 5.68421 20.5313V15.8443C5.68421 15.5855 5.47209 15.3756 5.21053 15.3756H0.473684ZM0.947368 16.313H4.73684V20.0626H0.947368V16.313ZM9 17.2537C8.73843 17.2537 8.52632 17.4636 8.52632 17.7224V18.6598C8.52632 18.9186 8.73843 19.1285 9 19.1285H17.5263C17.7879 19.1285 18 18.9186 18 18.6598V17.7224C18 17.4636 17.7879 17.2537 17.5263 17.2537H9Z" fill="#344054" />
            </svg>

          </span>
          <div>
            <h5 className="mb-0 text-gray-900 text-base font-semibold">
              {getTitle()}
            </h5>
          </div>
        </div>
        <button
          onClick={onDialogClose}
          className="text-gray-400 hover:text-gray-600"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      <div className="px-6 py-6 max-h-[70vh] overflow-y-auto">
        {/* Quiz Name */}
        <div className="flex mb-5">
          <label className="font-medium text-gray-700 text-sm w-1/3 pt-2">
            Quiz Name*
          </label>
          <div className="w-2/3 pl-2">
            <input
              className="h-9 pl-3 w-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              value={formData.quizName}
              placeholder="Quiz Name"
              onChange={(e) => handleInputChange('quizName', e.target.value)}
              disabled={isViewMode}
            />
            {formError.quizName && (
              <p className="text-xs text-red-600 mt-1">{formError.quizName}</p>
            )}
          </div>
        </div>

        {/* Questions List */}
        {formData.questions.map((question, index) => (
          <div key={question.id} className="mb-6 pb-6 border-b border-gray-200 last:border-b-0">
            {/* Question Type */}
            <div className="flex mb-5">
              <label className="font-medium text-gray-700 text-sm w-1/3 pt-2">
                Question Type*
              </label>
              <div className="w-2/3 pl-2">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm font-medium text-orange-500">Question {index + 1}</p>
                  {!isViewMode && formData.questions.length > 1 && (
                    <button
                      onClick={() => removeQuestion(question.id)}
                      type="button"
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-500 hover:bg-orange-600 transition-colors"
                    >
                      <svg
                        width="14"
                        height="16"
                        viewBox="0 0 17 19"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M11.6673 4.16683V3.50016C11.6673 2.56674 11.6673 2.10003 11.4857 1.74351C11.3259 1.42991 11.0709 1.17494 10.7573 1.01515C10.4008 0.833496 9.93407 0.833496 9.00065 0.833496H7.66732C6.7339 0.833496 6.26719 0.833496 5.91067 1.01515C5.59706 1.17494 5.3421 1.42991 5.18231 1.74351C5.00065 2.10003 5.00065 2.56674 5.00065 3.50016V4.16683M6.66732 8.75016V12.9168M10.0007 8.75016V12.9168M0.833984 4.16683H15.834M14.1673 4.16683V13.5002C14.1673 14.9003 14.1673 15.6004 13.8948 16.1351C13.6552 16.6055 13.2727 16.988 12.8023 17.2277C12.2675 17.5002 11.5674 17.5002 10.1673 17.5002H6.50065C5.10052 17.5002 4.40045 17.5002 3.86567 17.2277C3.39527 16.988 3.01282 16.6055 2.77313 16.1351C2.50065 15.6004 2.50065 14.9003 2.50065 13.5002V4.16683"
                          stroke="white"
                          strokeWidth="1.66667"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  )}
                </div>
                <Select
                  options={questionTypes}
                  value={question.questionType}
                  onChange={(selectedOption) => updateQuestion(question.id, 'questionType', selectedOption)}
                  placeholder="Select Question Type"
                  isDisabled={isViewMode}
                  isClearable={!isViewMode}
                  size="sm"
                />
                {question.errors?.questionType && (
                  <p className="text-xs text-red-600 mt-1">{question.errors.questionType}</p>
                )}
              </div>
            </div>
            {/* Dynamic Fields Based on Question Type */}
            {question.questionType && renderQuestionFields(question, index)}
          </div>
        ))}

      {/* Add Another Button */}
        {!isViewMode && (
          <div className="flex">
            <div className="w-1/3"></div>
            <div className="w-2/3 pl-2">
              <button
                className="w-full h-10 bg-orange-50 text-orange-500 text-sm font-medium rounded-lg hover:bg-orange-100 transition-colors flex items-center justify-center gap-2"
                onClick={addNewQuestion}
                type="button"
              >
                <span>Add another</span>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 3.333v9.334M3.333 8h9.334" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {formError.questions && (
          <p className="text-xs text-red-600 mt-2">{formError.questions}</p>
        )}
      </div>

      {/* Footer */}
      <div className="flex dailog-footer">
        <button
          className="mr-1.5 whiteBtnShadow btn border border-gray-100 block w-full rounded-lg h-10 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50 hover:border-gray-300 duration-100"
          onClick={onDialogClose}
          style={{ backgroundColor: '#FFF4E5' }}
          type="button"
        >
          Cancel
        </button>
        {!isViewMode && (
          <BtnPrimary
            disabled={buttonDisabled}
            onClick={handleSubmit}
            className="ml-1.5"
            style={{ backgroundColor: '#FF8E1C', borderColor: '#FF8E1C' }}
          >
            {buttonDisabled ? (
              <ThreeDotLoader />
            ) : (
              <>
                {isEditMode ? 'Update Quiz' : 'Add Quiz'}
               
              </>
            )}
          </BtnPrimary>
        )}
      </div>
    </Dialog>
  );
};

export default AddEditQuiz;